import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { adminAPI } from '../../utils/api';
import { formatDate, formatCurrency, getStatusBadgeClass, formatStatusText } from '../../utils/helpers';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import AdminTableToolbar from '../../components/admin/AdminTableToolbar';
import AdminPagination from '../../components/admin/AdminPagination';
import AdminTableLoadingOverlay from '../../components/admin/AdminTableLoadingOverlay';
import { useDebounce } from '../../hooks/useDebounce';

const FETCH_LIMIT = 500;

const AdminTradeDesk = () => {
  const [rawTrades, setRawTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [quoteData, setQuoteData] = useState({ quoted_price: '', fee: '', spread: '' });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    fetchTrades();
  }, [debouncedSearch]);

  const fetchTrades = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getTrades({
        search: debouncedSearch || undefined,
        skip: 0,
        limit: FETCH_LIMIT,
      });
      setRawTrades(response.data.trades);
      setPage(1);
    } catch (error) {
      toast.error('Failed to load trades');
    } finally {
      setLoading(false);
    }
  };

  const filteredTrades = statusFilter
    ? rawTrades.filter((t) => t.status === statusFilter)
    : rawTrades;
  const total = filteredTrades.length;
  const trades = filteredTrades.slice((page - 1) * pageSize, page * pageSize);

  const handleSendQuote = async () => {
    try {
      await adminAPI.sendQuote({
        trade_id: selectedTrade.id,
        quoted_price: parseFloat(quoteData.quoted_price),
        fee: parseFloat(quoteData.fee),
        spread: parseFloat(quoteData.spread),
      });
      toast.success('Quote sent to user');
      setSelectedTrade(null);
      setQuoteData({ quoted_price: '', fee: '', spread: '' });
      fetchTrades();
    } catch (error) {
      toast.error('Failed to send quote');
    }
  };

  const handleTradeAction = async (tradeId, status) => {
    try {
      await adminAPI.tradeAction({ trade_id: tradeId, status, txid: 'TXN-' + Date.now() });
      toast.success('Trade updated');
      fetchTrades();
    } catch (error) {
      toast.error('Action failed');
    }
  };

  const filterOptions = [
    { value: 'submitted', label: 'Submitted' },
    { value: 'in_review', label: 'In Review' },
    { value: 'quoted', label: 'Quoted' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'completed', label: 'Completed' },
    { value: 'declined', label: 'Declined' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  if (loading && rawTrades.length === 0) return <div className="flex items-center justify-center min-h-[60vh]"><div className="loading-spinner"></div></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="admin-trade-desk">
      <h1 className="text-2xl sm:text-4xl font-bold text-foreground">Trade Desk</h1>

      <AdminTableToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by user name or email..."
        filterValue={statusFilter}
        onFilterChange={(v) => { setStatusFilter(v); setPage(1); }}
        filterOptions={filterOptions}
        filterPlaceholder="All statuses"
        total={total}
        loading={loading}
      />

      <Card className="glass-card p-6 overflow-hidden">
        <AdminTableLoadingOverlay loading={loading}>
        <div className="admin-table-wrapper">
          <table>
            <thead>
              <tr className="border-b border-border">
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">User</th>
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Type</th>
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Asset</th>
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Amount</th>
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Status</th>
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Date</th>
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {trades.map((trade) => (
                <tr key={trade.id} className="table-row" data-testid={`trade-row-${trade.id}`}>
                  <td className="py-4">
                    <div>
                      <p className="text-foreground">{trade.user?.full_name || '—'}</p>
                      <p className="text-muted-foreground text-sm">{trade.user?.email || '—'}</p>
                    </div>
                  </td>
                  <td className="py-4 text-foreground capitalize">{trade.trade_type}</td>
                  <td className="py-4 text-foreground">{trade.asset_symbol}</td>
                  <td className="py-4 text-foreground">{trade.amount}</td>
                  <td className="py-4">
                    <span className={`badge ${getStatusBadgeClass(trade.status)}`}>
                      {formatStatusText(trade.status)}
                    </span>
                  </td>
                  <td className="py-4 text-muted-foreground text-sm">{formatDate(trade.created_at)}</td>
                  <td className="py-4">
                    {trade.status === 'submitted' && (
                      <Button size="sm" onClick={() => setSelectedTrade(trade)} data-testid={`send-quote-btn-${trade.id}`}>
                        Send Quote
                      </Button>
                    )}
                    {trade.status === 'accepted' && (
                      <Button size="sm" onClick={() => handleTradeAction(trade.id, 'completed')} className="btn-primary" data-testid={`complete-trade-btn-${trade.id}`}>
                        Complete
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <AdminPagination page={page} pageSize={pageSize} onPageSizeChange={(v) => { setPageSize(v); setPage(1); }} total={total} onPageChange={setPage} loading={loading} />
        </AdminTableLoadingOverlay>
      </Card>

      <Dialog open={!!selectedTrade} onOpenChange={() => setSelectedTrade(null)}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">Send Quote</DialogTitle>
          </DialogHeader>
          {selectedTrade && (
            <div className="space-y-4">
              <div>
                <p className="text-muted-foreground text-sm">Trade: {selectedTrade.trade_type} {selectedTrade.amount} {selectedTrade.asset_symbol}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Quoted Price (per unit)</Label>
                <Input type="number" step="0.01" value={quoteData.quoted_price} onChange={(e) => setQuoteData({ ...quoteData, quoted_price: e.target.value })} className="input-field mt-1" placeholder="0.00" required data-testid="quoted-price-input" />
              </div>
              <div>
                <Label className="text-muted-foreground">Fee</Label>
                <Input type="number" step="0.01" value={quoteData.fee} onChange={(e) => setQuoteData({ ...quoteData, fee: e.target.value })} className="input-field mt-1" placeholder="0.00" required data-testid="fee-input" />
              </div>
              <div>
                <Label className="text-muted-foreground">Spread</Label>
                <Input type="number" step="0.01" value={quoteData.spread} onChange={(e) => setQuoteData({ ...quoteData, spread: e.target.value })} className="input-field mt-1" placeholder="0.00" required data-testid="spread-input" />
              </div>
              <Button onClick={handleSendQuote} className="w-full btn-primary" data-testid="confirm-quote-button">
                Send Quote
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTradeDesk;
